import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { useApiQuery, type Api } from "../../../services/api";
import { gaEventViewItemListGptAnswerSources } from "../../../tools/analytics/domains/article.analytics";
import type { ConversationEngine } from "../../../domains/assistant/conversationEngine";
import type { GptSourceRef } from "../../../domains/gpt/transcript";
import { authorLine } from "../../../domains/article/utils/authorLine";
import { citedBesidesPaper } from "../../../domains/gpt/citation";
import { publicationYear, toChatSource } from "../../../domains/gpt/chatSource";
import { ChatSources } from "../../ui/ChatSources";
import { useSelectedGptSource } from "./GPTSourceSheet";
import { PaperCard } from "../../ui/PaperCard";

// The single, full vertical sources list rendered for one answer (the active
// Q/R pair in the conversation tabs). Every provenance reads as the same
// numbered row (`ChatSources`), the number being the one the answer's inline
// citation chips point at; internal references are hydrated into a journal, an
// author line and a study design through the article batch fetch first.
// Mounting this list IS the "viewed sources" signal — it is rendered only while
// the Sources tab is open for a turn.
//
// Chat-with-Paper draws its grounding paper as a CWYOP card above those rows:
// the paper is what the conversation is about, not a cited source, so the
// numbering (and the answer's chips) count only what was cited besides it.

type BatchArticle =
  Api["get"]["/webapp/public/articles/batch"]["output"]["body"][number];

export default function GPTSourcesList({
  sources,
  size,
  roomId,
  cwyopPaper,
  engine,
}: {
  sources: GptSourceRef[];
  // `row` in the Sources tab, `rail` in the desktop context panel.
  size: "row" | "rail";
  roomId: string | null;
  // Non-null in Chat-with-Paper: the paper renders as the CWYOP card with this
  // label ("Chat With Paper" / "Chat with abstract"), above the numbered rows
  // of what the answer cited besides it.
  cwyopPaper: { articleId: string; label: string } | null;
  // The engine of the conversation, which the list's view event reports.
  engine: ConversationEngine;
}): React.JSX.Element {
  const { articleLink } = usePageLinks(roomId);
  const [, setSelected] = useSelectedGptSource();
  const open = (source: GptSourceRef | undefined, citationNumber: number) => {
    setSelected(
      source === undefined ? null : { kind: "listed", source, citationNumber }
    );
  };
  const hasTrackedViewEventRef = useRef(false);

  const sourceIds = useMemo(
    () => [
      ...new Set(
        sources.flatMap((source) =>
          source.provenance === "juisci_internal" ? [source.id] : []
        )
      ),
    ],
    [sources]
  );
  const sourceIdsKey = useMemo(() => sourceIds.join(","), [sourceIds]);
  const sourcesDetails = useApiQuery(
    "/webapp/public/articles/batch",
    sourceIds.length === 0
      ? false
      : { query: { ids: sourceIds.join(",") }, durable: true }
  );
  const articleById = useMemo(
    () =>
      new Map(
        sourcesDetails.state === "success"
          ? sourcesDetails.body.map((article) => [article.id, article])
          : []
      ),
    [sourcesDetails]
  );
  // The position is the citation number: `citationNumbering` numbered the list in this order.
  const rowsOf = (cited: GptSourceRef[]) =>
    cited.map((source, index) => ({
      source: toChatSource(source, index, articleById, articleLink),
      number: index + 1,
    }));

  useEffect(() => {
    hasTrackedViewEventRef.current = false;
  }, [sourceIdsKey]);

  useEffect(() => {
    if (hasTrackedViewEventRef.current) return;
    if (sourcesDetails.state !== "success") return;
    if (sourcesDetails.body.length === 0) return;
    gaEventViewItemListGptAnswerSources({
      articles: sourcesDetails.body,
      engine,
    });
    hasTrackedViewEventRef.current = true;
  }, [sourcesDetails, sourceIdsKey]);

  if (cwyopPaper !== null) {
    const cited = citedBesidesPaper(sources, cwyopPaper.articleId);
    // The paper's own reference: what the turn listed it as, drawn from until
    // the batch read lands (and for good if that read fails).
    const paper = sources
      .flatMap((source) =>
        source.provenance === "juisci_internal" &&
        source.id === cwyopPaper.articleId
          ? [source]
          : []
      )
      .at(0);
    const article = articleById.get(cwyopPaper.articleId);
    return (
      <div className="flex flex-col gap-3">
        {paper !== undefined && (
          <CwyopPaperCard
            paper={paper}
            article={article === undefined ? null : article}
            label={cwyopPaper.label}
            articleLink={articleLink}
          />
        )}
        {/* A turn with no sources at all still says so, through the list's
            own empty state. */}
        {(cited.length > 0 || sources.length === 0) && (
          <ChatSources
            size={size}
            rows={rowsOf(cited)}
            onSelect={(index) => {
              open(cited.at(index), index + 1);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <ChatSources
      size={size}
      rows={rowsOf(sources)}
      // The sheet itself is mounted above the tabs, so a citation chip in the
      // answer opens the same one this row does.
      onSelect={(index) => {
        open(sources.at(index), index + 1);
      }}
    />
  );
}

/**
 * The paper a Chat-with-Paper conversation is about, as the CWYOP card. It is
 * the grounding of every turn rather than one citation among others, so it
 * sits above the numbered list instead of in it.
 */
function CwyopPaperCard({
  paper,
  article,
  label,
  articleLink,
}: {
  paper: Extract<GptSourceRef, { provenance: "juisci_internal" }>;
  // Null until the batch read lands, or when it failed or the article is gone.
  article: BatchArticle | null;
  label: string;
  articleLink: (article: { slug?: string }) => string;
}): React.JSX.Element {
  const journal =
    article !== null && article.journal !== null ? article.journal : null;
  const year = publicationYear(
    article === null ? null : article.publication_date
  );
  const dateLabel = [
    journal === null ? "" : journal.name,
    year === null ? "" : String(year),
  ]
    .filter((part) => part !== "")
    .join(" · ");
  // Same rendering as the general-chat card for the same article — the paper
  // is grounding a conversation, so its authorship must be as findable here as
  // in any other citation.
  const author =
    article === null ? "" : authorLine(article.primary_author, article.authors);
  const title =
    article !== null ? article.title : paper.title === null ? "" : paper.title;
  const slug = article !== null ? article.slug : paper.slug;
  const card = (
    <PaperCard
      variant="cwyop"
      label={label}
      dateLabel={dateLabel}
      title={title}
      author={author === "" ? null : author}
    />
  );
  return slug === null ? (
    card
  ) : (
    <Link to={articleLink({ slug })} className="no-underline">
      {card}
    </Link>
  );
}
